import { useState, useEffect, useMemo, useRef, startTransition } from "react";
import {
  Search, Library, CheckCircle2, X, Star, ArrowLeft, Loader2,
  ListChecks, Palette, Filter, Settings, Sparkles, BookOpen, Check, Layers,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ProgressBar } from "./ProgressBar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { PokemonCardTile } from "./PokemonCardTile";
import { BinderGrid } from "./BinderGrid";
import { AddToListButtons } from "./AddToListButtons";
import { computeVariants, variantsForMode } from "../lib/variants";
import { handleSetAssetError } from "../lib/images";
import { LABELS, VARIANT_ORDER, REVERSE_RARITIES } from "../config/variants";
import { getCollectionKey } from "../lib/collection";
import { fetchSet, setSetVariantRules } from "../lib/api";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { useSetAsset } from "../hooks/useSetAsset";

const CATEGORIES = ["Pokémon", "Dresseur", "Énergie"];

export const SetDetailView = ({
  setId, onBack, collection, conditions = {}, onStatusChange, onRemove, onCardClick,
  onBulkMarkStatus, onBulkRemove, mode = "normal", onModeChange,
  lists = [], onCreateList, onAddToList,
  customVariants = [], customApplications = [], onCreateCustomVariant, onApplyCustomVariant,
  onRemoveCustomApplication,
  masterRules = [], onAddMasterRule, onRemoveMasterRule,
  // Vue en lecture seule (collection d'un ami) : consultation uniquement,
  // aucun clic ne doit pouvoir écrire dans SA PROPRE collection par erreur.
  readOnly = false,
}) => {
  const [setData, setSetData] = useState(null);
  const [loading, setLoading] = useState(true);
  // true = coupure réseau pure (pas de réponse du serveur, rien en cache
  // local pour ce set) : à distinguer d'un set qui n'existe vraiment pas,
  // sinon une simple perte de connexion affichait « Collection non trouvée »
  // comme si la collection avait disparu — message trompeur et sans retry,
  // remonté par l'utilisateur comme de la "bugginess" générale (2026-10-03).
  const [networkError, setNetworkError] = useState(false);
  // Incrémenté par le bouton "Réessayer" pour relancer le fetch ci-dessous
  // sans dupliquer sa logique (startTransition, reset des filtres...).
  const [retryTick, setRetryTick] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState("all");
  // Raretés à MASQUER (pas à inclure) : permet de cacher d'un coup toutes les
  // Communes/Peu Communes/Rares pour ne garder que les tirages recherchés,
  // plutôt que de n'en choisir qu'une seule à la fois (retour utilisateur du
  // 2026-09-09).
  const [hiddenRarities, setHiddenRarities] = useState(() => new Set());
  const [illustratorFilter, setIllustratorFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  // Affichage "classeur" (une pochette par variante) — réservé aux modes Master.
  const [binderView, setBinderView] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkVariant, setBulkVariant] = useState("normal");
  const [showVariantRules, setShowVariantRules] = useState(false);
  const [ruleRarity, setRuleRarity] = useState(null);
  const [ruleSelected, setRuleSelected] = useState(() => new Set());
  const [applyingRule, setApplyingRule] = useState(false);
  // Panneau "Variantes perso" (application par set)
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [cvSelectedId, setCvSelectedId] = useState(null);
  const [cvRarity, setCvRarity] = useState(null); // null = tout le set
  const [cvNewName, setCvNewName] = useState("");
  const [applyingCv, setApplyingCv] = useState(false);
  // Panneau « Réservé au Master Set + »
  const [showMasterPanel, setShowMasterPanel] = useState(false);
  const [mpRarity, setMpRarity] = useState(null);   // null = tout le set
  const [mpVariant, setMpVariant] = useState(null); // null = toute la carte
  const [mpEverywhere, setMpEverywhere] = useState(false);
  const [mpBusy, setMpBusy] = useState(false);
  const online = useNetworkStatus();

  // Clic sur un badge variant : cycle owned → wanted → rien (shift+clic = wanted direct)
  const handleVariantClick = (cardId, variantKey, currentStatus, shiftKey) => {
    if (readOnly) return;
    if (shiftKey) {
      if (currentStatus === "wanted") onRemove(cardId, variantKey);
      else onStatusChange(cardId, variantKey, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") onStatusChange(cardId, variantKey, "owned");
      else onRemove(cardId, variantKey);
    }
  };

  useEffect(() => {
    setLoading(true);
    setNetworkError(false);
    setSearchQuery("");
    setFilterTab("all");
    setHiddenRarities(new Set());
    setIllustratorFilter(null);
    setCategoryFilter(null);
    setSelectMode(false);
    setSelectedIds(new Set());
    setShowVariantRules(false);
    setRuleRarity(null);
    setShowCustomPanel(false);
    setCvSelectedId(null);
    setCvRarity(null);
    setCvNewName("");
    setShowMasterPanel(false);
    setMpRarity(null);
    setMpVariant(null);
    setMpEverywhere(false);
    // La reponse charge le rendu de 100-400+ tuiles d'un coup : sans
    // `startTransition`, ce commit unique et synchrone peut retarder de
    // plusieurs frames la mise a jour du compositeur pour les elements
    // `position: fixed` (barre du bas), qui restent visuellement figes a
    // leur ancienne position pendant que le contenu, lui, a deja change en
    // dessous - confirme par enregistrement d'ecran (2026-08-07), le bug
    // apparaissait precisement a l'ouverture d'un set. `startTransition`
    // laisse React fractionner ce gros rendu, redonnant la main au
    // navigateur entre les morceaux.
    fetchSet(setId)
      .then(set => {
        startTransition(() => {
          setSetData(set);
          setLoading(false);
        });
      })
      .catch(err => {
        console.error("Error fetching set:", err);
        // Pas de err.response = aucune réponse du serveur (coupure réseau),
        // à distinguer d'un vrai 404 : withOfflineFallback a déjà tenté le
        // cache local avant de relancer cette erreur, donc ici il n'y a
        // vraiment rien à montrer — mais ce n'est pas pour autant que la
        // collection "n'existe pas".
        setNetworkError(!err?.response);
        setLoading(false);
      });
  }, [setId, retryTick]);

  // Quand une variante perso est appliquée/retirée ailleurs (fiche carte, ou
  // panneau ci-dessous), la liste globale des applications change → on
  // rafraîchit discrètement le set pour que les badges apparaissent tout de
  // suite, sans avoir à quitter puis revenir. On saute le 1er passage (déjà
  // couvert par le chargement initial).
  const cvAppsFirstRun = useRef(true);
  useEffect(() => {
    if (cvAppsFirstRun.current) { cvAppsFirstRun.current = false; return; }
    fetchSet(setId)
      .then(set => setSetData(set))
      .catch(err => console.error("Error refreshing set:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customApplications, masterRules]);

  // Chez un ami, /sets renvoie les règles du compte connecté : elles ne le concernent
  // pas, on affiche donc toutes ses cartes.
  const cards = useMemo(() => {
    const list = setData?.cards || [];
    return readOnly ? list.map(({ master_plus_only, ...card }) => card) : list;
  }, [setData, readOnly]);

  // Statuts (possédée/recherchée) des variantes qui comptent en Set normal
  // — source unique pour les stats et les filtres.
  const cardStatuses = (card) => variantsForMode(card, "normal")
    .filter(v => v.type !== "loading")
    .map(v => collection[getCollectionKey(card.id, v.type)])
    .filter(Boolean);

  // Types de variantes comptés pour cette carte dans le mode COURANT (même
  // règle que les badges affichés).
  const variantTypesFor = (card) =>
    variantsForMode(card, mode).filter(v => v.type !== "loading").map(v => v.type);

  // Cartes du mode courant : celles réservées au Master Set + n'y sont pas.
  const modeCards = useMemo(
    () => cards.filter(card => variantsForMode(card, mode).length > 0),
    [cards, mode],
  );

  // Mode "normal" : 1 carte = 1 case, peu importe la variante possédée.
  // Mode "master" : chaque variante réelle (normal/holo + reverse/1re éd./promo)
  // compte comme une case à part. Mode "master+" : idem + les variantes perso.
  // On réutilise computeVariants pour rester identique aux badges affichés.
  const setStats = useMemo(() => {
    if (mode === "master" || mode === "masterplus") {
      let owned = 0, wanted = 0, total = 0;
      modeCards.forEach(card => {
        const types = variantTypesFor(card);
        total += types.length;
        types.forEach(type => {
          const status = collection[getCollectionKey(card.id, type)];
          if (status === "owned") owned++;
          if (status === "wanted") wanted++;
        });
      });
      return { owned, wanted, total };
    }
    let owned = 0, wanted = 0;
    modeCards.forEach(card => {
      const statuses = cardStatuses(card);
      if (statuses.includes("owned")) owned++;
      if (statuses.includes("wanted")) wanted++;
    });
    return { owned, wanted, total: modeCards.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeCards, collection, mode]);

  // Valeurs disponibles pour les filtres, dérivées de TOUTES les cartes du
  // set (pas de la liste déjà filtrée, sinon les menus se videraient au fur
  // et à mesure qu'on filtre).
  const allRarities = useMemo(() => {
    const set = new Set();
    cards.forEach(c => { if (c.rarity) set.add(c.rarity); });
    return Array.from(set).sort();
  }, [cards]);

  const allIllustrators = useMemo(() => {
    const set = new Set();
    cards.forEach(c => { if (c.illustrator) set.add(c.illustrator); });
    return Array.from(set).sort();
  }, [cards]);

  const availableCategories = useMemo(() => {
    const set = new Set();
    cards.forEach(c => { if (c.category) set.add(c.category); });
    return CATEGORIES.filter(c => set.has(c));
  }, [cards]);

  const filteredCards = modeCards.filter(card => {
    if (searchQuery && !card.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (card.rarity && hiddenRarities.has(card.rarity)) return false;
    if (illustratorFilter && card.illustrator !== illustratorFilter) return false;
    if (categoryFilter && card.category !== categoryFilter) return false;
    if (filterTab !== "all") {
      if (mode === "master" || mode === "masterplus") {
        // En master(+), la case est la VARIANTE, pas la carte : une carte
        // avec 3 variantes dont 1 possédée doit apparaître à la fois dans
        // "Possédées" (elle en a une) ET "Manquantes" (il en manque encore) —
        // sinon posséder une seule variante retirait toute la carte de
        // "Manquantes" même avec plusieurs variantes encore à trouver
        // (retour utilisateur du 2026-09-09 : 81 manquantes affichées en
        // haut, seulement 9 cartes dans l'onglet).
        const variantStatuses = variantTypesFor(card).map(t => collection[getCollectionKey(card.id, t)]);
        if (filterTab === "owned" && !variantStatuses.includes("owned")) return false;
        if (filterTab === "wanted" && !variantStatuses.includes("wanted")) return false;
        if (filterTab === "missing" && variantStatuses.length > 0 && variantStatuses.every(s => s === "owned")) return false;
      } else {
        const statuses = cardStatuses(card);
        if (filterTab === "owned"   && !statuses.includes("owned"))  return false;
        if (filterTab === "wanted"  && !statuses.includes("wanted")) return false;
        if (filterTab === "missing" &&  statuses.includes("owned"))  return false;
      }
    }
    return true;
  });

  const completion = setStats.total > 0 ? Math.round(setStats.owned / setStats.total * 100) : 0;
  const activeFilterCount = [illustratorFilter, categoryFilter].filter(Boolean).length + (hiddenRarities.size > 0 ? 1 : 0);

  const allFilteredSelected = filteredCards.length > 0 && filteredCards.every(c => selectedIds.has(c.id));

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleCardSelection = (cardId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const selectAllOrNone = () => {
    setSelectedIds(allFilteredSelected ? new Set() : new Set(filteredCards.map(c => c.id)));
  };

  // Seules les cartes sélectionnées qui possèdent réellement le variant
  // choisi sont retenues — les autres sont simplement ignorées.
  const selectedItems = () => Array.from(selectedIds)
    .map(cardId => cards.find(c => c.id === cardId))
    .filter(card => card && computeVariants(card).some(v => v.type === bulkVariant))
    .map(card => ({ card_id: card.id, variant: bulkVariant }));

  const runBulkMark = async (status) => {
    await onBulkMarkStatus(selectedItems(), status);
    setSelectedIds(new Set());
  };

  const runBulkRemove = async () => {
    await onBulkRemove(selectedItems());
    setSelectedIds(new Set());
  };

  // On ne vide PAS la sélection après ajout : on peut ainsi ajouter la même
  // sélection à plusieurs listes, ou plusieurs fois. Le compteur du bouton
  // (AddToListButtons) matérialise chaque ajout ; "Quitter la sélection"
  // remet à zéro.
  const addSelectionToList = async (listId) => {
    await onAddToList(listId, selectedItems());
  };

  const clearFilters = () => {
    setHiddenRarities(new Set());
    setIllustratorFilter(null);
    setCategoryFilter(null);
  };

  const toggleHiddenRarity = (r) => {
    setHiddenRarities(prev => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };

  // Raccourci "occulter les communes/peu communes/rares" : mêmes raretés que
  // REVERSE_RARITIES (déjà la référence des tirages "de base" dans l'app),
  // restreintes à celles présentes dans CE set.
  const hideCommonTiers = () => {
    setHiddenRarities(new Set(allRarities.filter(r => REVERSE_RARITIES.has(r))));
  };

  // Panneau "corriger les variants par rareté" (Phase 6) : choisir une
  // rareté du set pré-remplit la checklist avec les variants actuellement
  // détectés (souvent ce qu'il faut corriger), puis on écrase pour toutes
  // les cartes de cette rareté via PUT /sets/{id}/variant-rules.
  const pickRuleRarity = (rarity) => {
    setRuleRarity(rarity);
    const sample = cards.find(c => c.rarity === rarity);
    setRuleSelected(new Set(
      sample ? computeVariants(sample).filter(v => v.type !== "loading").map(v => v.type) : []
    ));
  };

  const toggleRuleType = (type) => {
    setRuleSelected(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const applyVariantRule = async () => {
    if (!ruleRarity) return;
    setApplyingRule(true);
    try {
      await setSetVariantRules(setId, { [ruleRarity]: Array.from(ruleSelected) });
      const updated = await fetchSet(setId);
      setSetData(updated);
      setRuleRarity(null);
    } catch (err) { console.error(err); }
    finally { setApplyingRule(false); }
  };

  // Application d'une variante perso à ce set (toutes les cartes, ou une
  // rareté). Après coup on refetch le set pour voir les nouveaux badges.
  const applyCustomVariantToSet = async () => {
    if (!cvSelectedId) return;
    setApplyingCv(true);
    try {
      // Le rafraîchissement des badges est géré par l'effet sur
      // customApplications (ci-dessus) — inutile de re-fetch ici.
      await onApplyCustomVariant(cvSelectedId, { scope: "set", setId, rarity: cvRarity });
    } catch (err) { console.error(err); }
    finally { setApplyingCv(false); }
  };

  const createAndSelectCustomVariant = async () => {
    const name = cvNewName.trim();
    if (!name || !onCreateCustomVariant) return;
    const created = await onCreateCustomVariant({ name });
    if (created?.id) setCvSelectedId(created.id);
    setCvNewName("");
  };

  const addMasterRuleFromPanel = async () => {
    setMpBusy(true);
    try {
      await onAddMasterRule(mpEverywhere && mpVariant && !mpRarity
        ? { variant: mpVariant }
        : { variant: mpVariant, set_id: setId, rarity: mpRarity });
      setMpRarity(null);
      setMpVariant(null);
      setMpEverywhere(false);
    } catch (err) { console.error(err); }
    finally { setMpBusy(false); }
  };

  // Règles qui touchent ce set : les siennes, celles valables partout, et celles
  // posées sur une de ses cartes.
  const setMasterRules = masterRules.filter(r =>
    r.card_id ? cards.some(c => c.id === r.card_id) : (!r.set_id || r.set_id === setId));
  const masterRuleLabel = (r) => {
    const what = r.variant ? LABELS[r.variant] || r.variant : (r.card_id ? "Toute la carte" : r.rarity ? "Toutes ses variantes" : "Tout le set");
    if (r.card_id) return `${cards.find(c => c.id === r.card_id)?.name || r.card_id} n°${cards.find(c => c.id === r.card_id)?.localId || "?"} : ${what}`;
    if (r.rarity) return `${r.rarity} : ${what}`;
    return r.set_id ? `Ce set : ${what}` : `Toutes les collections : ${what}`;
  };

  // Variantes perso posées sur ce set ou une de ses cartes, pour pouvoir les retirer.
  const setCustomApps = customApplications
    .filter(a => a.scope === "set" ? a.set_id === setId : cards.some(c => c.id === a.card_id))
    .map(a => {
      const variant = customVariants.find(v => v.id === a.variant_id);
      const card = cards.find(c => c.id === a.card_id);
      const target = a.scope === "set" ? (a.rarity || "Tout le set") : `${card?.name || a.card_id} n°${card?.localId || "?"}`;
      return { id: a.id, label: `${variant?.name || "Variante"} : ${target}` };
    });

  const selectedCustomVariant = customVariants.find(v => v.id === cvSelectedId);
  // Id ET URL tirés du même objet : en passant d'un set à l'autre, `setId`
  // change avant que `setData` ne soit rechargé, et l'ancien couple « nouvel
  // id + logo du set précédent » enregistrait ce logo sous le nom du nouveau
  // set dans le cache hors ligne (Nuit Noire affichait celui du 30ᵉ).
  const logoSrc = useSetAsset(setData?.id, "logo", setData?.logo);
  const symbolSrc = useSetAsset(setData?.id, "symbol", setData?.symbol);

  return (
    <div>
      <Button variant="ghost" className="mb-4 text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={onBack}>
        <ArrowLeft className="w-4 h-4 mr-2" />Retour aux collections
      </Button>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-red-500" />
        </div>
      ) : setData ? (
        <>
          <div className="mb-6 bg-gradient-to-r from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-start gap-5 flex-wrap">
              {logoSrc && (
                <img src={logoSrc} alt={setData.name} className="h-20 object-contain"
                  onError={handleSetAssetError} />
              )}
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs text-red-400 uppercase tracking-widest mb-1">{setData.serie?.name}</div>
                <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                  {symbolSrc && <img src={symbolSrc} alt="" className="w-12 h-12 object-contain drop-shadow" onError={handleSetAssetError} />}
                  {setData.name}
                </h2>
                <div className="flex items-center gap-4 text-sm text-zinc-400 flex-wrap">
                  <span>{setData.releaseDate}</span><span>•</span><span>{setStats.total} cartes</span>
                  {/* TCGdex n'a pas cette collection en français (ex. Trésors
                      Légendaires) : on sert l'anglaise plutôt que rien. */}
                  {setData.lang === "en" && (
                    <span className="text-amber-500/90 border border-amber-500/40 rounded px-2 py-0.5 text-xs">
                      Version anglaise (aucune donnée française chez TCGdex)
                    </span>
                  )}
                  {/* Hors ligne, étape 2 : pas de suivi par-requête "vient du
                      cache" — si l'appareil est hors ligne et qu'on affiche
                      quand même quelque chose, ça vient forcément du cache
                      local (cf. lib/api.js withOfflineFallback). */}
                  {!online && (
                    <span className="text-sky-400/90 border border-sky-500/40 rounded px-2 py-0.5 text-xs">
                      Hors ligne — données en cache
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right min-w-0">
                <div className="flex items-center justify-start sm:justify-end flex-wrap gap-2 mb-2">
                  {readOnly ? (
                    <span className="text-xs text-zinc-500 border border-zinc-700 rounded px-2 py-1">
                      {mode === "master" ? "Master Set" : mode === "masterplus" ? "Master Set +" : "Set normal"}
                    </span>
                  ) : (
                    <>
                      <Button size="sm" variant="outline"
                        className={`border-zinc-700 text-white ${mode === "normal" ? "bg-zinc-800" : ""}`}
                        onClick={() => onModeChange?.("normal")}>
                        Set normal
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`border-zinc-700 text-white ${mode === "master" ? "bg-zinc-800" : ""}`}
                        onClick={() => onModeChange?.("master")}>
                        Master Set
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`border-zinc-700 text-white ${mode === "masterplus" ? "bg-zinc-800" : ""}`}
                        title="Master Set, tes variantes perso et ce que tu as réservé au Master Set +"
                        onClick={() => onModeChange?.("masterplus")}>
                        Master Set +
                      </Button>
                    </>
                  )}
                  {/* La vue classeur n'a de sens qu'en Master Set : c'est là
                      qu'on suit chaque variante séparément. */}
                  {mode !== "normal" && (
                    <Button size="sm" variant="outline"
                      className={`border-zinc-700 text-white ${binderView ? "bg-zinc-800" : ""}`}
                      title="Afficher chaque variante dans sa propre pochette, comme dans un classeur"
                      onClick={() => setBinderView(v => !v)}>
                      <BookOpen className="w-4 h-4 mr-1.5" />Classeur
                    </Button>
                  )}
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Complétion</div>
                <div className={`text-4xl font-bold ${completion >= 100 ? "text-amber-300" : completion >= 67 ? "text-emerald-400" : completion >= 34 ? "text-amber-400" : "text-red-400"}`}>{completion}%</div>
                <div className="text-xs text-zinc-400">{setStats.owned} / {setStats.total} possédées</div>
              </div>
            </div>
            <ProgressBar size="lg" className="mt-4" pct={completion} owned={setStats.owned} total={setStats.total} showCount={false} />
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-3">
            <div className="relative flex-1 sm:min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input placeholder="Rechercher dans cette collection..." className="search-input pl-10"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            {!readOnly && (
              <>
                <Button
                  variant="outline"
                  className={`border-zinc-700 text-white ${selectMode ? "bg-blue-500/20 border-blue-500 text-blue-400" : ""}`}
                  onClick={toggleSelectMode}>
                  <ListChecks className="w-4 h-4 mr-2" />
                  {selectMode ? "Quitter la sélection" : "Mode sélection"}
                </Button>
                <Button
                  variant="outline"
                  className={`border-zinc-700 text-white ${showVariantRules ? "bg-zinc-800" : ""}`}
                  onClick={() => setShowVariantRules(prev => !prev)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Corriger les variants
                </Button>
                <Button
                  variant="outline"
                  className={`border-zinc-700 text-white ${showCustomPanel ? "bg-zinc-800" : ""}`}
                  onClick={() => setShowCustomPanel(prev => !prev)}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Variantes perso
                </Button>
                <Button
                  variant="outline"
                  className={`border-zinc-700 text-white ${showMasterPanel ? "bg-zinc-800" : ""}`}
                  onClick={() => setShowMasterPanel(prev => !prev)}>
                  <Layers className="w-4 h-4 mr-2" />
                  Réservé au Master Set +
                </Button>
              </>
            )}
          </div>

          {!readOnly && showMasterPanel && (
            <div className="mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <div className="text-sm text-zinc-400 mb-3">
                Sors du Master Set ce que tu ne veux pas compléter : ça ne compte plus qu'en Master Set +.
                Une carte réservée en entier disparaît aussi du Set normal. Ce réglage ne concerne que ton compte.
                Pour une seule carte, passe par sa fiche.
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                      {mpRarity || "Tout le set"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
                    <DropdownMenuItem onClick={() => setMpRarity(null)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      Tout le set
                    </DropdownMenuItem>
                    {allRarities.map(r => (
                      <DropdownMenuItem key={r} onClick={() => setMpRarity(r)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                        {r}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                      {mpVariant ? LABELS[mpVariant] : "Toutes les variantes"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white">
                    <DropdownMenuItem onClick={() => { setMpVariant(null); setMpEverywhere(false); }} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      Toutes les variantes
                    </DropdownMenuItem>
                    {VARIANT_ORDER.map(type => (
                      <DropdownMenuItem key={type} onClick={() => setMpVariant(type)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                        {LABELS[type]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {mpVariant && !mpRarity && (
                  <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                    <input type="checkbox" checked={mpEverywhere} onChange={(e) => setMpEverywhere(e.target.checked)} />
                    Dans toutes les collections
                  </label>
                )}
                <Button size="sm" disabled={mpBusy} className="bg-emerald-600 hover:bg-emerald-500"
                  onClick={addMasterRuleFromPanel}>
                  {mpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réserver"}
                </Button>
              </div>
              {setMasterRules.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {setMasterRules.map(r => (
                    <span key={r.id} className="inline-flex items-center gap-1.5 text-xs text-zinc-200 bg-zinc-800 rounded-full pl-3 pr-1.5 py-1">
                      {masterRuleLabel(r)}
                      <button title="Remettre dans le Master Set" className="text-zinc-400 hover:text-white"
                        onClick={() => onRemoveMasterRule(r.id).catch(err => console.error(err))}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {!readOnly && showCustomPanel && (
            <div className="mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <div className="text-sm text-zinc-400 mb-3">
                Applique une variante perso à ce set — soit à toutes les cartes, soit à une seule rareté
                (ex. « Reverse Poké Ball » sur les Communes). Elle comptera dans le mode Master Set +.
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {/* Choix de la variante perso */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                      {selectedCustomVariant ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 rounded-sm inline-block" style={{ background: selectedCustomVariant.color }} />
                          {selectedCustomVariant.name}
                        </span>
                      ) : "Choisir une variante"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
                    {customVariants.length === 0 ? (
                      <DropdownMenuItem disabled className="text-zinc-500">Aucune variante — crées-en une ci-dessous</DropdownMenuItem>
                    ) : customVariants.map(v => (
                      <DropdownMenuItem key={v.id} onClick={() => setCvSelectedId(v.id)}
                        className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                        <span className="w-4 h-4 rounded-sm mr-2 inline-block" style={{ background: v.color }} />
                        {v.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Portée : tout le set ou une rareté */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                      {cvRarity || "Tout le set"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
                    <DropdownMenuItem onClick={() => setCvRarity(null)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      Tout le set
                    </DropdownMenuItem>
                    {allRarities.map(r => (
                      <DropdownMenuItem key={r} onClick={() => setCvRarity(r)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                        {r}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button size="sm" disabled={!cvSelectedId || applyingCv}
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
                  onClick={applyCustomVariantToSet}>
                  {applyingCv ? <Loader2 className="w-4 h-4 animate-spin" /> : "Appliquer"}
                </Button>
              </div>

              {/* Création rapide d'une nouvelle variante */}
              <div className="flex items-center gap-2">
                <Input placeholder="...ou crée une nouvelle variante" value={cvNewName}
                  onChange={(e) => setCvNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") createAndSelectCustomVariant(); }}
                  className="max-w-xs h-9" />
                <Button size="sm" variant="outline" className="border-zinc-700 text-white"
                  disabled={!cvNewName.trim()} onClick={createAndSelectCustomVariant}>
                  Créer
                </Button>
              </div>
              {setCustomApps.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {setCustomApps.map(a => (
                    <span key={a.id} className="inline-flex items-center gap-1.5 text-xs text-zinc-200 bg-zinc-800 rounded-full pl-3 pr-1.5 py-1">
                      {a.label}
                      <button title="Retirer cette variante perso" className="text-zinc-400 hover:text-white"
                        onClick={() => Promise.resolve(onRemoveCustomApplication?.(a.id)).catch(err => console.error(err))}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {!readOnly && showVariantRules && (
            <div className="mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <div className="text-sm text-zinc-400 mb-3">
                Force les variantes disponibles pour toutes les cartes d'une rareté de ce set (corrige les erreurs TCGdex).
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                      {ruleRarity || "Choisir une rareté"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
                    {allRarities.map(r => (
                      <DropdownMenuItem key={r} onClick={() => pickRuleRarity(r)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                        {r}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {ruleRarity && VARIANT_ORDER.map(type => {
                  const checked = ruleSelected.has(type);
                  return (
                    <button key={type} onClick={() => toggleRuleType(type)}
                      className={`variant-chip ${checked ? "bg-zinc-600 ring-2 ring-white/60" : "bg-zinc-800 opacity-60"}`}>
                      {LABELS[type]}
                    </button>
                  );
                })}
              </div>
              {ruleRarity && (
                <Button size="sm" disabled={applyingRule} className="bg-emerald-600 hover:bg-emerald-500"
                  onClick={applyVariantRule}>
                  {applyingRule
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : `Appliquer à "${ruleRarity}"`}
                </Button>
              )}
            </div>
          )}

          {/* Filtres rareté / illustrateur / catégorie */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <Filter className="w-4 h-4 text-zinc-500" />
            {allRarities.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline"
                    className={`border-zinc-700 text-white ${hiddenRarities.size > 0 ? "bg-zinc-800" : ""}`}>
                    Raretés{hiddenRarities.size > 0 ? ` (${allRarities.length - hiddenRarities.size}/${allRarities.length})` : ""}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-80 overflow-y-auto">
                  <button onClick={(e) => { e.preventDefault(); hideCommonTiers(); }}
                    className="w-full text-left px-2 py-1.5 text-sm rounded-sm text-amber-400 hover:bg-zinc-800">
                    Masquer Commune / Peu Commune / Rare
                  </button>
                  <button onClick={(e) => { e.preventDefault(); setHiddenRarities(new Set()); }}
                    className="w-full text-left px-2 py-1.5 text-sm rounded-sm text-zinc-400 hover:bg-zinc-800">
                    Tout afficher
                  </button>
                  <div className="my-1 border-t border-zinc-800" />
                  {allRarities.map(r => {
                    const shown = !hiddenRarities.has(r);
                    return (
                      <button key={r} onClick={(e) => { e.preventDefault(); toggleHiddenRarity(r); }}
                        className="w-full flex items-center gap-2 text-left px-2 py-1.5 text-sm rounded-sm hover:bg-zinc-800">
                        <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                          shown ? "bg-emerald-500 border-emerald-500" : "border-zinc-600"}`}>
                          {shown && <Check className="w-3 h-3 text-zinc-950" />}
                        </span>
                        {r}
                      </button>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {allIllustrators.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline"
                    className={`border-zinc-700 text-white ${illustratorFilter ? "bg-zinc-800" : ""}`}>
                    {illustratorFilter || "Illustrateur"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
                  <DropdownMenuItem onClick={() => setIllustratorFilter(null)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                    Tous les illustrateurs
                  </DropdownMenuItem>
                  {allIllustrators.map(i => (
                    <DropdownMenuItem key={i} onClick={() => setIllustratorFilter(i)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      {i}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {availableCategories.length > 1 && availableCategories.map(cat => (
              <Button key={cat} size="sm" variant="outline"
                className={`border-zinc-700 text-white ${categoryFilter === cat ? "bg-zinc-800" : ""}`}
                onClick={() => setCategoryFilter(categoryFilter === cat ? null : cat)}>
                {cat}
              </Button>
            ))}
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />Réinitialiser
              </Button>
            )}
          </div>

          {!readOnly && selectMode && (
            <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <span className="text-sm text-zinc-400">{selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>
              <Button size="sm" variant="outline" className="border-zinc-700 text-white" onClick={selectAllOrNone}>
                {allFilteredSelected ? "Tout désélectionner" : "Tout sélectionner"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                    <Palette className="w-4 h-4 mr-2" />Variant : {LABELS[bulkVariant] || bulkVariant}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white">
                  {VARIANT_ORDER.map(type => (
                    <DropdownMenuItem key={type} onClick={() => setBulkVariant(type)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                      {LABELS[type]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex-1" />
              <Button size="sm" disabled={selectedIds.size === 0}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
                onClick={() => runBulkMark("owned")}>
                <CheckCircle2 className="w-4 h-4 mr-2" />Marquer possédées
              </Button>
              <Button size="sm" disabled={selectedIds.size === 0}
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-40"
                onClick={() => runBulkMark("wanted")}>
                <Star className="w-4 h-4 mr-2" />Marquer recherchées
              </Button>
              <Button size="sm" disabled={selectedIds.size === 0} variant="outline"
                className="border-zinc-700 text-white disabled:opacity-40"
                onClick={runBulkRemove}>
                <X className="w-4 h-4 mr-2" />Retirer
              </Button>
              <div className="w-full">
                <AddToListButtons lists={lists} disabled={selectedIds.size === 0}
                  onAddToList={addSelectionToList} onCreateList={onCreateList} />
              </div>
            </div>
          )}

          {/* Les 4 onglets ne tiennent pas sur la largeur d'un telephone : sans
              `overflow-x-auto` ici, c'est la PAGE entiere qui devenait
              defilable vers la gauche. Le debordement reste enferme dans sa
              propre bande. */}
          <Tabs value={filterTab} onValueChange={setFilterTab} className="mb-5 max-w-full overflow-x-auto">
            <TabsList className="bg-zinc-900/50 border border-zinc-800 h-auto flex-nowrap">
              <TabsTrigger value="all" className="shrink-0 px-2 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-zinc-800 data-[state=active]:text-white">
                <Library className="w-4 h-4 mr-1.5" />Toutes ({setStats.total})
              </TabsTrigger>
              <TabsTrigger value="owned" className="shrink-0 px-2 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />Possédées ({setStats.owned})
              </TabsTrigger>
              <TabsTrigger value="missing" className="shrink-0 px-2 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                <X className="w-4 h-4 mr-1.5" />Manquantes ({setStats.total - setStats.owned})
              </TabsTrigger>
              <TabsTrigger value="wanted" className="shrink-0 px-2 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
                <Star className="w-4 h-4 mr-1.5" />Recherchées ({setStats.wanted})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {modeCards.length < cards.length && (
            <div className="mb-4 text-sm text-zinc-500">
              {cards.length - modeCards.length} carte{cards.length - modeCards.length > 1 ? "s" : ""} réservée{cards.length - modeCards.length > 1 ? "s" : ""} au Master Set + {cards.length - modeCards.length > 1 ? "ne sont pas affichées" : "n'est pas affichée"}.
              {onModeChange && (
                <button className="ml-2 underline hover:text-white" onClick={() => onModeChange("masterplus")}>
                  Passer en Master Set +
                </button>
              )}
            </div>
          )}

          {filteredCards.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <Library className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune carte trouvée.</p>
            </div>
          ) : binderView && mode !== "normal" && !selectMode ? (
            <BinderGrid
              cards={filteredCards}
              collectionMap={collection}
              conditionMap={conditions}
              cardVariants={(card) => variantsForMode(card, mode)}
              onCardClick={(c) => onCardClick(c, filteredCards.map(x => x.id))}
              onVariantClick={handleVariantClick}
              officialCount={setData?.cardCount?.official}
              setLogo={setData?.logo}
            />
          ) : (
            <div className="card-grid">
              {filteredCards.map(card => (
                <PokemonCardTile
                  key={card.id}
                  card={card}
                  collectionMap={collection}
                  conditionMap={conditions}
                  onCardClick={(c) => onCardClick(c, filteredCards.map(x => x.id))}
                  onVariantClick={handleVariantClick}
                  cardVariants={variantsForMode(card, mode)}
                  selectMode={selectMode}
                  selected={selectedIds.has(card.id)}
                  onToggleSelect={toggleCardSelection}
                />
              ))}
            </div>
          )}
        </>
      ) : networkError ? (
        <div className="text-center py-20 text-zinc-500">
          <p className="mb-4">Impossible de charger cette collection — vérifie ta connexion.</p>
          <Button variant="outline" onClick={() => setRetryTick(t => t + 1)}>Réessayer</Button>
        </div>
      ) : (
        <div className="text-center py-20 text-zinc-500">Collection non trouvée</div>
      )}
    </div>
  );
};

// ============================================================
// MAIN APP
